import { sharedRemoteFeedback, sharedFeedbackValue, sharedFeedbackText, isSharedRemoteFeedback, type SharedRemoteFeedback } from "./sharedFeedbackPresentation";
import { getI18n, useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import source from "./locales/en/vpnManagement";

export type VpnKey = keyof typeof source;
export type VpnPluralKey = "configsCount" | "uploadingConfigs" | "entriesCount" | "countriesCount" | "deactivateRoutes" | "disableRoutes" | "locationsCount";
export type VpnValues = Record<string, string | number>;
export type VpnFeedback = string | SharedRemoteFeedback | { key: VpnKey; values?: VpnValues; issues?: VpnFeedback[]; detail?: VpnFeedback };
export function useVpnManagementLanguage(): void { useTranslation("vpnManagement"); }
export function vpnLocale(): string { return getI18n()?.resolvedLanguage ?? getI18n()?.language ?? "en"; }
export function vpnNumber(value: number): string { return new Intl.NumberFormat(vpnLocale()).format(value); }
/** Only source-owned identities enter this lookup; external labels and diagnostics remain literal. */
export function vpnText(key: VpnKey, values?: VpnValues): string {
  const engine = getI18n();
  const replace = values && Object.fromEntries(Object.entries(values).map(([name, value]) => [name, typeof value === "number" ? vpnNumber(value) : value]));
  if (!engine?.isInitialized) return (source[key]).replace(/{{\s*(\w+)\s*}}/g, (_match, name: string) => String(replace?.[name] ?? ""));
  return (engine as unknown as { t: (key: VpnKey, options: Record<string, unknown>) => string }).t(key, { ns: "vpnManagement", keySeparator: false, replace, interpolation: { escapeValue: false, skipOnVariables: true } });
}
export const vpnLocal = (key: VpnKey, values?: VpnValues): Extract<VpnFeedback, { key: VpnKey }> => ({ key, values });
export const vpnMessage = <T extends VpnFeedback | null | undefined>(value: T): T extends null | undefined ? T : string => {
  if (typeof value !== "object" || value === null) return value as T extends null | undefined ? T : string;
  if (isSharedRemoteFeedback(value)) return sharedFeedbackText(value) as T extends null | undefined ? T : string;
  let values = value.values;
  if (value.detail) values = { ...values, detail: vpnMessage(value.detail) };
  if (value.issues) values = { ...values, message: value.issues.map(issue => vpnMessage(issue)).join(", ") };
  if (["backendVpnBusy", "backendOpenvpnBusy", "backendJobActionFailed", "backendJobExit"].includes(value.key) && values) {
    const actions = { activate: "backendActionActivate", deactivate: "backendActionDeactivate", delete: "backendActionDelete", upload: "backendActionUpload", "nordvpn-generate": "backendActionGenerate" } as const;
    const action = values.action;
    if (typeof action === "string" && Object.prototype.hasOwnProperty.call(actions, action)) values = { ...values, action: vpnText(actions[action as keyof typeof actions]) };
  }
  return vpnText(value.key, values) as T extends null | undefined ? T : string;
};
export function vpnNodes(key: VpnKey, values: Record<string, ReactNode>): ReactNode[] {
  const nodes = Object.values(values);
  const markers = Object.fromEntries(Object.keys(values).map((name, index) => [name, `\uFFF0${index}\uFFF1`]));
  return vpnText(key, markers).split(/(\uFFF0\d+\uFFF1)/g).map(part => { const match = /^\uFFF0(\d+)\uFFF1$/.exec(part); return match ? nodes[Number(match[1])] : part; });
}

export function vpnUnit(value: number, unit: string, digits = 0): string { return new Intl.NumberFormat(vpnLocale(), { style: "unit", unit, unitDisplay: "short", maximumFractionDigits: digits }).format(value); }
export function vpnRelative(value: number, unit: Intl.RelativeTimeFormatUnit): string { return new Intl.RelativeTimeFormat(vpnLocale(), { numeric: "auto", style: "short" }).format(value, unit); }
export function vpnList(values: string[], type: "conjunction" | "disjunction" = "conjunction"): string { return new Intl.ListFormat(vpnLocale(), { style: "long", type }).format(values); }
export function vpnPlural(key: VpnPluralKey, count: number, values?: VpnValues): string {
 const candidate = `${key}_${new Intl.PluralRules(vpnLocale()).select(count)}`;
 const chosen = Object.prototype.hasOwnProperty.call(source, candidate) ? candidate as VpnKey : `${key}_other` as VpnKey;
 return vpnText(chosen, { ...values, count });
}

/** Only explicit, well-formed source provenance selects catalog copy. */
export function vpnRemote(presentation: unknown, fallback: VpnFeedback, allowIssues = true, depth = 0): VpnFeedback {
  if (depth > 3) return fallback;
  const shared = sharedFeedbackValue(typeof fallback === "string" ? fallback : undefined, presentation);
  if (shared) return shared;
  if (!presentation || typeof presentation !== "object" || !Object.prototype.hasOwnProperty.call(presentation, "code")) return fallback;
  const descriptor = presentation as { code?: unknown; params?: unknown; issues?: unknown; detail?: unknown };
  if (typeof descriptor.code !== "string" || !descriptor.code.startsWith("vpn.backend")) return fallback;
  const key = descriptor.code.slice(4) as VpnKey;
  if (!Object.prototype.hasOwnProperty.call(source, key)) return fallback;
  if (key === "backendInvalidBodyDetails") {
    if (!allowIssues || !Array.isArray(descriptor.issues) || descriptor.issues.length > 100) return fallback;
    const issues: VpnFeedback[] = [];
    for (const item of descriptor.issues) {
      if (!item || typeof item !== "object" || typeof item.message !== "string") return fallback;
      issues.push(vpnRemote(item.presentation, item.message, false, depth + 1));
    }
    return { key, issues };
  }
  const params = descriptor.params;
  const values: VpnValues = {};
  for (const match of source[key].matchAll(/{{\s*(\w+)\s*}}/g)) {
    const name = match[1]!;
    if (!params || typeof params !== "object" || !Object.prototype.hasOwnProperty.call(params, name)) return fallback;
    const value: unknown = (params as Record<string, unknown>)[name];
    if (typeof value !== "string" && !(typeof value === "number" && Number.isFinite(value))) return fallback;
    values[name] = value;
  }
  return { key, values, ...(typeof values.detail === "string" && descriptor.detail !== undefined ? { detail: vpnRemote(descriptor.detail, values.detail, false, depth + 1) } : {}) };
}
export function vpnResponseFeedback(response: unknown, fallback: VpnFeedback): VpnFeedback {
  const shared = sharedRemoteFeedback(response);
  if (shared) return shared;
  if (!response || typeof response !== "object") return fallback;
  const data = response as { message?: unknown; presentation?: unknown; error?: unknown };
  if (typeof data.message === "string") return vpnRemote(data.presentation, data.message);
  if (data.error && typeof data.error === "object") {
    const nested = data.error as { message?: unknown; presentation?: unknown };
    if (typeof nested.message === "string") return vpnRemote(nested.presentation, nested.message);
  }
  return vpnRemote(data.presentation, fallback);
}

type RoutingDiagnostic = { lastError: string | null; lastErrorPresentation?: unknown };
/** Select raw text and provenance from the same event; retain the existing snapshot fallback. */
export function vpnRoutingError(live: RoutingDiagnostic | null | undefined, snapshot: RoutingDiagnostic | null | undefined): string | null {
  const diagnostic = typeof live?.lastError === "string" ? live : snapshot;
  return diagnostic?.lastError != null ? vpnMessage(vpnRemote(diagnostic.lastErrorPresentation, diagnostic.lastError)) : null;
}
